/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ApolloError } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { Spacer, theme } from "@nordcloud/gnui";
import { ChartTableSortField } from "~/generated/graphql";
import {
  LoaderWrap,
  ErrorWrap,
  Pagination,
  Option,
  TableData,
  EstatePeriod,
  SortDirection,
} from "~/components";
import { SimpleError } from "~/tools";
import { EstateGroupingsCell, EstateGroupingsTable } from "../EstateGroupings";
import { EstateRecordsLoader } from "../EstateRecordsLoader";
import { ChartParametersBar } from "../ParametersBar";

type Props = {
  loading: boolean;
  sortField: ChartTableSortField;
  sortDirection: SortDirection;
  sortOptions: Option[];
  onSortSelect: (value: ChartTableSortField) => void;
  onSortDirectionSelect: (direction: SortDirection) => void;
  tableCount: number;
  columns: ColumnDef<TableData>[];
  data: TableData[];
  period: EstatePeriod;
  firstColumnWidth?: string;
  error?: ApolloError | Error | SimpleError;
};

export function EstateRecordsGroupingTable({
  loading,
  tableCount,
  sortField,
  sortDirection,
  sortOptions,
  onSortDirectionSelect,
  onSortSelect,
  error,
  columns,
  data,
  firstColumnWidth,
  period,
}: Props) {
  return (
    <>
      <ChartParametersBar
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={sortOptions}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortDirectionSelect}
      />
      <LoaderWrap loading={loading} Component={EstateRecordsLoader}>
        <ErrorWrap error={error}>
          <div css={{ width: "100%", overflow: "scroll" }}>
            <EstateGroupingsTable
              columns={columns}
              data={data}
              firstColumnWidth={firstColumnWidth}
            >
              {(cell) => (
                <>
                  <EstateGroupingsCell
                    cell={cell}
                    background={getCellBackgroundColor(cell.row.index, period)}
                  />
                </>
              )}
            </EstateGroupingsTable>
          </div>
        </ErrorWrap>
      </LoaderWrap>
      <Spacer height={theme.spacing.spacing03} />
      <Pagination count={tableCount ?? 0} />
    </>
  );
}

const getCellBackgroundColor = (rowIndex: number, period: EstatePeriod) => {
  return rowIndex === 0 && period === EstatePeriod.RANGE
    ? theme.color.background.ui03
    : undefined;
};
